/**
 * @file     value-collection.js - implement the ValueCollection class, which is a class building
 *           Arrays of values from other Arrays of values, using continuation-passing style to
 *           implement the .except() operator.  The pattern in .except() can be followed when
 *           inheriting from ValueCollection to implement a specific kind of list with a richer set of
 *           operations.
 *
 *           This collection of values has a very important and surprising property: the operators are
 *           carefully build so that new String('abc') and 'abc' are considered to be exactly equal
 *           values. This is done so that end users can use tagged boxed strings to mean special things,
 *           and this library can just ignore the tagging.  Care is taken to ensure that the boxed
 *           Strings are persisted throughout the set.  Note that creating ValueCollections with both
 *           new String('abc') and 'abc' might cause this property to be lost. If this property is
 *           necessary, make sure you specify options = { unique: true }.
 *
 *           The code is carefully written so that:
 *           - extending by inheritance is easy
 *           - ValueCollections are never mutated as side-effects and so are copied
 *           - ValueCollections can be purposefully mutated via a values() function supplied to the
 *             constructor
 *
 *           This makes them well-suited for use in configuration grammars where words like 'all' have
 *           dynamic values based on context.
 *
 *           An additional class, ValueCollectionMemo, helps to manipulate ValueCollections via late
 *           binding/evaluation. This class knows how to memoize the operations on a ValueCollection,
 *           but wait to actually excute them until the ::evaluate() method is run.
 *
 * @author   Wes Garland, wes@distributive.network
 * @date     Jun 2025
 *
 * @example  new ValueCollection(['abc', 'abc', 'def', 'ghi'], { unique: true }).except('def').values() => [ 'abc', 'def' ]
 */
'use strict';

const { assert } = require('dcp/common/dcp-assert');
const { uniq, useColour } = require('.');

exports.ValueCollection = ValueCollection;
exports.ValueCollectionMemo = ValueCollectionMemo;

/**
 * ValueCollection constructor
 *
 * @param {Function|Array|ValueCollection} values
 *        The values that this collection will have. Static-valued collections should be given either a
 *        ValueCollection or an Array. Dynamic-valued collections should be given a function which
 *        returns an Array.
 * @param {Object} options
 *        Behaviours for this instance, passed along when the instance is dup'd internally; eg. ::except().
 *          .unique: ignore duplicate elements
 *          .inspectOptions: passed to util.inspect() in node
 */
function ValueCollection(values, options)
{
  if (values instanceof ValueCollection)
    values = Array.from(values.values());
  assert(Array.isArray(values) || typeof values === 'function');

  this.options = options;
  if (Array.isArray(values))
    this.values = () => this.options?.unique ? uniq(values.slice()) : values.slice();
  else
    this.values = () => this.options?.unique ? uniq(values()) :       values();
}

/**
 * Locate a value within the collection. Really only meant for internal use. Throws if not found.
 * The value can be either a boxed or an unboxed string, the comparison is made only only on the
 * string data, not the boxing object.
 */
ValueCollection.prototype.indexOf = function ValueCollection$indexOf(value)
{
  const array = this.values();
  var idx = array.indexOf(value);
  if (idx !== -1)
    return idx;
  for (idx = 0; idx < array.length; idx++)
    if (String(array[idx]) === value)
      return idx;

  const error = new Error(`no such value: ${value}`);
  error.code = 'ENOENT';
  throw error;
}

/** @returns the number of elements in a collection */
Object.defineProperty(ValueCollection.prototype, 'length', {
  get: function ValueCollection$$length$getter() {
    return this.values().length
  },
});

/**
 * Returns a new ValueCollection instance, with the same values as the parent but no dupes.
 */
ValueCollection.prototype.findUniq = function ValueCollection$findUniq()
{
  return new this.constructor(uniq(this.values()), this.options);
}

/**
 * Locate elements in the collection which are tagged. If a test function is supplied, returns
 * only the tagged elements which pass the test.
 */
ValueCollection.prototype.findTagged = function ValueCollection$findTagged(testFn)
{
  var values = this.values().filter(value => value instanceof String);
  if (testFn)
    values = values.filter(testFn);
  return new this.constructor(values, this.options);
}

/**
 * Locate elements in the collection which are not tagged.
 */
ValueCollection.prototype.findNotTagged = function ValueCollection$findNotTagged()
{
  var values = this.values().filter(value => !(value instanceof String));
  return new this.constructor(values, this.options);
}

/**
 * Returns a new ValueCollection instance, with only values that past test()
 */
ValueCollection.prototype.filter = function ValueCollection$filter(testFn)
{
  return new this.constructor(this.values().filter(test => {
    return test instanceof String ? testFn(String(test)) : testFn(test);
  }), this.options);
}

/**
 * Checks a value collection for a value; if it is found, return that value. The comparison is
 * made without regard to value tagging, but the actual value (eg potentially tagged) that is in
 * the collection is returned.
 *
 * If the value is not in the collection, returns undefined.
 */
ValueCollection.prototype.includes = function ValueCollection$includes(value)
{
  if (value instanceof String)
    value = String(value);
  const values = this.values();
  const exact = values.includes(value); /* fast path */
  if (exact)
    return value;

  for (let idx = 0; idx < values.length; idx++)
    if (String(values[idx]) === value)
      return values[idx];

  return undefined;
}

/**
 * Returns a new ValueCollection instance, which has only the values that aren't in the argument list
 */
ValueCollection.prototype.except = function ValueCollection$except()
{
  const values = this.values().slice();
  const ret = new this.constructor(values, this.options);

  for (let value of arguments)
  {
    const idx = ret.indexOf(value);
    values.splice(idx, 1);
  }

  return ret;
}

/**
 * Tag each value in a collection with tagFn. Trying to tag a value in the
 * collection that doesn't exist will throw an exception.
 *
 * @param {function}           tagFn       function that knows how to apply tags
 * @param {Array}              tagValues   list of values to tag
 * @param {Array | undefined}  tagDetails  opaque details for tagging function
 */
ValueCollection.prototype.tag = function ValueCollection$tag(tagFn, tagValues, tagDetails)
{
  const values = this.values();
  if (tagDetails)
    assert(tagDetails.length === tagValues.length);

  for (let i=0; i < tagValues.length; i++)
  {
    /* Odd lookup so that we find the *actual*, potentially tagged value, in the collection, and pass
     * that into the tagging function for further mutation if necessary.
     */
    const idx = this.indexOf(tagValues[i]);
    values[idx] = tagFn(values[idx], tagDetails?.[i]);
  }
  return new this.constructor(values, this.options);
}

ValueCollection.prototype.toString = function ValueCollection$$inspect() {
  return `[${this.constructor.name} ${this.values().join(', ')}]`;
};

if (require('dcp/common/dcp-env').platform === 'nodejs')
{
  const { requireNative } = require('dcp/dcp-client/webpack-native-bridge');
  const util = requireNative('util');
  const inspect = util.inspect.custom;
  ValueCollection.prototype[inspect] = function ValueCollection$$__inspect__()
  {
    const inspectOptions = Object.assign({ colors: useColour }, this.options?.inspectOptions);
    const inspectFn = (value) => util.inspect(value, inspectOptions);
    return `[${this.constructor.name} ${this.values().map(inspectFn)}]`;
  }
}

/**
 * ValueCollectionMemo constructor.
 */
function ValueCollectionMemo()
{
  this.opers = {};
  this.opers.except = [];
}

/**
 * Operation Factory - used to build additional methods that can be memoized
 * @param {string} oper    name of the operation we want to be able to memoize
 */
ValueCollectionMemo.operFactory = function valueCollectionMemoOperFactory(oper)
{
  function valueCollectionMemoOper()
  {
    /* eslint-disable no-invalid-this */
    const next = new this.constructor();
    next.oper = oper;
    next.args = arguments;
    next.prev = this;
    return next;
    /* eslint-enable no-invalid-this */
  }
  valueCollectionMemoOper.__oper = oper;
  return valueCollectionMemoOper;
}
ValueCollectionMemo.prototype.except = ValueCollectionMemo.operFactory('except');

/**
 * Evaluate the ValueCollection and its operation changes now. Use values to give meaning to the first
 * (left-most) memo in a CPS chain, then apply the modifier functions in order (by walking the ->prev
 * pointers backwards).
 *
 * @param {ValueCollection|Array|function} values
 *        The values for the head of the chain; if not an instance of ValueCollection, the values are
 *        used as the arguments to the constructor identified by the static property 'memoFor' on the
 *        constructor. This semantic also applies to classes derived from ValueCollectionMemo.
 *
 * @param {object} options [optional]
 *        Passed to ::memoFor ctor when it is used because of values. If memoFor is missing, it is
 *        automatically supplied when using the base class, but throws if using a derived class.
 *
 * @returns {ValueCollection}
 */
ValueCollectionMemo.prototype.evaluate = function evaluate(values, options)
{
  var memos = [];
  for (let memo = this; memo.prev; memo  = memo.prev) // eslint-disable-line consistent-this
    memos.push(memo);

  if (!(values instanceof ValueCollection))
  {
    let Ctor = this.constructor.memoFor;
    if (!Ctor && this.constructor === ValueCollectionMemo)
      Ctor = ValueCollection;
    if (!Ctor)
      throw new Error(`missing ${this.constructor.name}::memoFor static property`);
    values = new Ctor(values, options);
  }
  for (let memo; (memo = memos.pop());)
    values = values[memo.oper].apply(values, memo.args);
  return values;
}
